import { CommonModule } from '@angular/common';
import {
  Component,
  Output,
  ViewChild,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  EmailOptIn,
  PreferenceProfileResponseData,
} from '@digital-platform/rs-cms/domain';
import { CmsFooterService } from '@digital-platform/rs-cms/services';
import { InlineSpinnerComponent } from '@digital-platform/rs-shared/ui';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { ButtonComponent } from '../button/button.component';
import { CtaButtonComponent } from '../cta-button/cta-button.component';
import { InputComponent } from '../input/input.component';

export enum EmailOptinResponseType {
  USER_UPDATED = 'userUpdated',
  USER_NEW = 'userNew',
}

@Component({
  selector: 'digital-platform-email-opt-in',
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    CtaButtonComponent,
    ButtonComponent,
    ReactiveFormsModule,
    InlineSpinnerComponent,
  ],
  templateUrl: './email-opt-in.component.html',
  styleUrls: ['./email-opt-in.component.scss'],
})
export class EmailOptInComponent
  extends BasePTypeComponent
  implements EmailOptIn, OnInit
{
  @ViewChild(InputComponent, { static: false })
  private inputComponent!: InputComponent;
  @Output() sendValue: EventEmitter<string> = new EventEmitter();

  private subscriptionInProgress: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  public form: FormGroup<{
    email: FormControl<string>;
  }>;
  public override type = 'emailOptIn' as const;
  public defaultButtonStyle = {
    tid: 6091,
    name: 'Secondary Inverted (ADA)',
    ctaStyle: 'btn-secondary inverted',
  };
  public defaultButtonSize = {
    tid: 6141,
    name: 'L',
    ctaSize: 'btn-lg',
  };
  public errorMessage = 'Please enter a valid email address';
  public submitSuccess = false;

  // Submit success response types for displaying correct message to user
  public responseTypes = EmailOptinResponseType;
  public responseType: EmailOptinResponseType | undefined;
  public subscriptionInProgress$: Observable<boolean>;

  constructor(private _subject: CmsFooterService) {
    super();
    this.form = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.maxLength(60),
        Validators.pattern(
          /^(?=.{6,60}$)(?!\.)(?!.*?\.\.)([a-zA-Z0-9_.+-]{1,55})@(?!\.)[a-zA-Z0-9.-]+\.([a-zA-Z]{2,25})$/
        ),
      ]),
    });
    this.subscriptionInProgress$ = this.subscriptionInProgress.asObservable();
  }

  ngOnInit(): void {
    this._subject.emailInputError$.subscribe((res) => {
      this.subscriptionInProgress.next(false);
      this.errorMessage = res.errorMessage;
      this.form.controls['email'].setErrors({ incorrect: true });
    });

    this._subject.emailInputSuccess$.subscribe(
      (res: PreferenceProfileResponseData | boolean) => {
        this.subscriptionInProgress.next(false);
        this.responseType = this.getResponseType(res);
        this.submitSuccess = true;
        this.resetForm();
      }
    );
  }

  /**
   * Determines the response type for a preference creation or
   * update to assist in generating the appropriate response
   * @param res
   * @returns
   */
  getResponseType(res: PreferenceProfileResponseData | boolean) {
    if (typeof res === 'boolean') {
      return EmailOptinResponseType.USER_UPDATED;
    }

    return EmailOptinResponseType.USER_NEW;
  }

  resetForm() {
    this.form.reset();
    this.form.markAsUntouched();

    setTimeout(() => {
      this.submitSuccess = false;
    }, 6000);
  }

  submitEmail() {
    if (this.form.valid) {
      this.subscriptionInProgress.next(true);
    }
    this.getInput();
  }

  getInput() {
    if (this.form.valid) {
      this._subject.emailInput$.next(this.form.get('email').value);
    }
  }
}
